import { useClaimedSellers } from "@/queries/customer/claimed-sellers";
import { useProductsPricing } from "@/queries/products/productsPricing";
import { useSellerOffer } from "@/queries/seller-offer";
import { useSellersMap } from "@/queries/sellers";
import { formatPrice, getPercentageDiff } from "@/utils/money";
import {
  resolveOfferPriceState,
  type OfferPriceState
} from "@/utils/offerPriceState";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { getHandlingTimeLabel } from "@repo/api-sdk";
import { useMemo } from "react";

export interface ProductDetailDialogData {
  // Pricing & availability from API
  priceCalculatedFormatted: string;
  priceCalculatedAmount: number;
  priceOriginalFormatted?: string;
  priceOriginalAmount?: number;
  savingsFormatted?: string;
  savingsPercentage?: string;
  status: SalonStockerHttpTypes.StoreSellerOfferStatus;
  handlingTimeLabel: string;
  currencyCode: string;

  // Seller info
  sellerName: string;
  sellerId: string;

  // The variant this single offer is for (used to add it to a saved list).
  variantId: string;

  // For future API data
  supplierInfo: null; // TODO: implement when API ready
  productInfo: null; // TODO: implement when API ready
  shippingInfo: null; // TODO: implement when API ready
}

export interface UseProductDetailDialogDataReturn {
  data: ProductDetailDialogData | undefined;
  isLoading: boolean;
  isError: boolean;
  isPending: boolean;
  /**
   * Whether `data`'s price may be shown (KAN-343). Anything but `ready` means
   * the offer belongs to a seller the salon has (or may have) claimed, and the
   * trade price is not in yet or could not be fetched; the offer endpoint's
   * price is then the retail one and must not be shown.
   */
  priceState: OfferPriceState;
}

export interface UseProductDetailDialogDataProps {
  offerId: string;
  /**
   * The offer's product, used to ask products-pricing for the price a claimed
   * salon is quoted. The offer endpoint does not say which product it is for.
   */
  productId?: string;
  enabled?: boolean;
}

export function useProductDetailDialogData({
  offerId,
  productId,
  enabled = true
}: UseProductDetailDialogDataProps): UseProductDetailDialogDataReturn {
  const {
    data: offerResponse,
    isLoading: isOfferLoading,
    isError: isOfferError,
    isPending: isOfferPending
  } = useSellerOffer(offerId, undefined, { enabled });

  const { data: sellersMap, isLoading: isSellersLoading } = useSellersMap();

  // The offer endpoint's price is the retail one. For a seller the salon has
  // claimed (or may have, while claims load) the price has to come from
  // products-pricing instead, so that is only asked for in that case.
  const claims = useClaimedSellers();
  const sellerId = offerResponse?.offer?.seller_id;
  const needsTradePrice =
    sellerId !== undefined &&
    resolveOfferPriceState(sellerId, claims, "pending") !== "ready";
  const { data: pricing, status: pricingStatus } = useProductsPricing({
    product_ids: enabled && needsTradePrice && productId ? [productId] : []
  });

  const pricedOffer = useMemo(() => {
    for (const product of pricing?.products ?? []) {
      for (const variant of product.variants) {
        const found = variant.seller_offers.find((o) => o.id === offerId);
        if (found) return found;
      }
    }
    return undefined;
  }, [pricing, offerId]);

  let priceState: OfferPriceState = "ready";
  if (needsTradePrice && sellerId !== undefined) {
    // Without a product to price there is no trade price to be had.
    const status = productId ? pricingStatus : "error";
    priceState = resolveOfferPriceState(sellerId, claims, status);
    // Priced fine but this offer is not in the response: nothing to trust.
    if (priceState === "ready" && !pricedOffer) priceState = "error";
  }

  const data = useMemo((): ProductDetailDialogData | undefined => {
    if (!offerResponse?.offer) {
      return undefined;
    }

    const offer = offerResponse.offer;
    // Never carry the retail price in `data` while it must not be shown.
    const calculatedPrice =
      priceState !== "ready"
        ? {
            calculated_amount: 0,
            original_amount: 0,
            currency_code: offer.calculated_price.currency_code
          }
        : needsTradePrice && pricedOffer
          ? pricedOffer.calculated_price
          : offer.calculated_price;

    const priceCalculatedAmount = calculatedPrice.calculated_amount;
    const priceOriginalAmount = calculatedPrice.original_amount;
    const currencyCode = calculatedPrice.currency_code || "AUD";

    const priceCalculatedFormatted = formatPrice(priceCalculatedAmount);

    const hasSavings = priceOriginalAmount > priceCalculatedAmount;

    const priceOriginalFormatted = hasSavings
      ? formatPrice(priceOriginalAmount)
      : undefined;

    const savingsAmount = hasSavings
      ? priceOriginalAmount - priceCalculatedAmount
      : 0;

    const savingsFormatted = hasSavings
      ? formatPrice(savingsAmount)
      : undefined;

    const savingsPercentage = hasSavings
      ? getPercentageDiff(priceOriginalAmount, priceCalculatedAmount)
      : undefined;

    const seller = sellersMap?.[offer.seller_id];
    const sellerName = seller?.name || "Unknown seller";

    return {
      priceCalculatedFormatted,
      priceCalculatedAmount,
      priceOriginalFormatted,
      priceOriginalAmount: hasSavings ? priceOriginalAmount : undefined,
      savingsFormatted,
      savingsPercentage,
      status: offer.status,
      handlingTimeLabel: getHandlingTimeLabel(offer.handling_time),
      currencyCode,
      sellerName,
      sellerId: offer.seller_id,
      variantId: offer.variant_id,
      // Future API data placeholders
      supplierInfo: null,
      productInfo: null,
      shippingInfo: null
    };
  }, [offerResponse, sellersMap, priceState, needsTradePrice, pricedOffer]);

  return {
    data,
    isLoading: isOfferLoading || isSellersLoading,
    isError: isOfferError,
    isPending: isOfferPending,
    priceState
  };
}
